import React from 'react';
import { useParams } from 'react-router-dom';
import 'twin.macro';
import { useDataLoader } from '../common/hooks';
import { getItemDetails } from 'services/dumpert';
import { Icon } from '../icon';
import { DumpertLogoIcon, DumpertSingleDLogoIcon } from 'icons';
import { MetaTags } from '../meta';
import { ItemDetailsMedia } from '../item/ItemDetailsMedia';
import { PreferenceContext, AppContext } from '../context';

export const Embed = () => {
  const { id: currentItemId } = useParams();
  const { result, error, isLoading } = useDataLoader(getItemDetails)({ itemId: currentItemId });

  if (isLoading) {
    return (
      <div tw='absolute inset-0 bg-stone-900 flex justify-center items-center' role='status'>
        <span tw='sr-only'>Loading content...</span>
        <div tw='h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand border-r-transparent'></div>
      </div>
    );
  }

  if (error) {
    const errorMessage =
      typeof error === 'object'
        ? error.message || 'Er is een onbekende fout opgetreden'
        : String(error);

    return (
      <div
        tw='absolute inset-0 bg-stone-900 flex flex-col justify-center items-center text-stone-50'
        role='alert'
      >
        <DumpertLogoIcon tw='h-10 text-brand mb-4' />
        <h2 tw='text-xl font-bold mb-2'>Oeps, er ging iets mis</h2>
        <p tw='text-center'>{errorMessage}</p>
      </div>
    );
  }

  if (!result?.data?.items?.[0]) {
    return (
      <div
        tw='absolute inset-0 bg-stone-900 flex flex-col justify-center items-center text-stone-50'
        role='alert'
      >
        <DumpertLogoIcon tw='h-10 text-brand mb-4' />
        <h2 tw='text-xl font-bold'>Item niet gevonden</h2>
      </div>
    );
  }

  const item = result.data.items[0];
  const isNsfw = item.nsfw;
  const isVideo = item.media_type === 'VIDEO';
  const itemUrl = `/item/${currentItemId}`;

  // Default context values to use in embed view
  const preferenceContextValue = {
    preference: { upNextActive: false },
    setPreference: () => {},
  };

  const appContextValue = {
    slideIn: { active: false },
    windowSize: { width: 1024, height: 768, adBreakPoint: 'DESKTOP' },
    setTwitchLiveStream: () => {},
    setSlideIn: () => {},
  };

  return (
    <div tw='absolute inset-0 bg-stone-900 overflow-hidden z-50'>
      <MetaTags item={item} embedded />

      {isNsfw && (
        <section
          tw='absolute inset-0 flex flex-col gap-2 justify-center items-center overflow-hidden z-50 px-16 text-center font-bold text-stone-50'
          aria-live='polite'
        >
          <DumpertLogoIcon tw='h-10 text-brand' />
          <p>
            HOI GLUURDER. DIT ITEM BEVAT SCHOKKENDE BEELDEN. GA NAAR{' '}
            <a
              href={itemUrl}
              target='_blank'
              rel='noopener noreferrer'
              tw='text-brand hover:underline focus:underline'
              aria-label='Bekijk dit item op Dumpert.nl (opent in nieuw venster)'
              tabIndex='0'
            >
              DUMPERT.NL
            </a>{' '}
            EN ZET EVENTUEEL 'TOON NSFW ITEMS' AAN OM DIT ITEM TE BEKIJKEN.
          </p>
        </section>
      )}

      {!isNsfw &&
        (isVideo ? (
          <AppContext.Provider value={appContextValue}>
            <PreferenceContext.Provider value={preferenceContextValue}>
              <ItemDetailsMedia item={item} index={0} />
            </PreferenceContext.Provider>
          </AppContext.Provider>
        ) : (
          <a
            href={itemUrl}
            target='_blank'
            tw='h-full w-full block'
            rel='noopener noreferrer'
            aria-label='Bekijk dit item op Dumpert.nl (opent in nieuw venster)'
            tabIndex='0'
          >
            <div
              tw="absolute inset-0 flex justify-center items-center overflow-hidden z-50
                  after:content-['']
                  after:block
                  after:w-14
                  after:h-14
                  after:bg-stone-50
                  after:absolute    
                  after:z-0"
            >
              <Icon
                Type={DumpertSingleDLogoIcon}
                size='xlarge'
                color='brand'
                tw='absolute z-20 hover:scale-125 focus:scale-125 transition-all shadow-xl'
                aria-hidden='true'
              />
            </div>
            <img
              src={item.still}
              tw='w-full h-full object-cover'
              alt={item.title || 'Dumpert content'}
              loading='eager'
            />
          </a>
        ))}
    </div>
  );
};
